import { baseApi } from '@/Redux/baseApi';
import type { MenuChannel, MenuVersionStatus } from '@/lib/shared-types';
import {
  MENU_VERSIONS,
  branchAssortmentPath,
  menuVersionPath,
  menuVersionPreviewPath,
} from './menuGovernanceRoutes';

/**
 * # The whole menu-governance wire contract, pinned in one place
 *
 * Read directly from `src/modules/menu-governance/` on the unmerged
 * `feat/menu-governance` backend worktree — `dto/menu-version.dto.ts` for the
 * request bodies, `menu-version.service.ts` for the response shapes (nothing
 * here is decorated with `@ApiResponse`, so every interface below is read off
 * what the service actually returns, not off Swagger), `publish-preview.ts`
 * for the preview, `branch-menu-resolver.ts` for the assortment read. Same
 * discipline `Pages/Pricing/pricingListsApi.ts` keeps for the same reason: the
 * backend is still moving, so a late rename is a one-file fix here rather than
 * a rewrite across five screens.
 *
 * ## Reads only
 *
 * Every **write** in this feature (create, update, rules, submit, approve,
 * reject, publish, cancel, rollback) goes through the `api` axios client
 * directly, from the component that performs it — the same split
 * `Pages/Pricing` keeps and for the same reason: a publish or a rollback needs
 * the refusal's HTTP status to choose a sentence (see `menuGovernanceRoutes.ts`
 * for why it is status rather than a code), and RTK Query's mutation error
 * shape flattens that away. What lives here is the cache RTK Query is actually
 * good at: a version list, a version detail, a preview, a branch assortment —
 * revalidated by tag after every one of those writes.
 *
 * ## The pagination envelope is NOT `{ data, meta }`
 *
 * Most of this dashboard's list endpoints answer `{ data, meta: { total } }`,
 * which is what `Hooks/useApi.ts`'s `rowsOf()` unwraps. `GET
 * /menu-governance/versions` does not: `MenuVersionService.findAll` returns
 * `common/dto/pagination.dto.ts`'s `paginate()` verbatim, which is
 * `{ items, total, page, limit, pages }`. `rowsOf()` would silently read this
 * as zero rows — `p?.data` is `undefined` on every page — so `MenuVersionList`
 * is spelled out below instead of reused from the generic envelope.
 */

/** A person, exactly as every `*By` relation on a version is selected. */
export interface MenuGovernancePerson {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

export interface MenuVersionBranchTarget {
  branchId: string;
  branch: { id: string; name: string; code: string };
}

/** The version a rollback restored, named on the version IT created. */
export interface MenuVersionRevertRef {
  id: string;
  versionNo: number;
  name: string;
}

/** One `GET /menu-governance/versions` row, and the common half of the detail. */
export interface MenuVersionRow {
  id: string;
  versionNo: number;
  name: string;
  notes: string | null;
  status: MenuVersionStatus;
  /** ISO. Null on a version that has never been published or scheduled. */
  effectiveFrom: string | null;
  createdById: string;
  submittedById: string | null;
  approvedById: string | null;
  publishedById: string | null;
  /**
   * A raw id, not a name: `MenuVersionService`'s `VERSION_INCLUDE` expands
   * `createdBy` / `approvedBy` / `publishedBy` into `{ id, firstName, lastName }`
   * but leaves `rolledBackBy` unexpanded, so "who rolled this back" has no
   * name to show without a second `GET /users/:id` this screen does not make.
   * Flagged for the backend — the same include shape for all four columns
   * would remove the gap in one line.
   */
  rolledBackById: string | null;
  decisionNote: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
  rolledBackAt: string | null;
  revertsId: string | null;
  createdAt: string;
  updatedAt: string;
  targets: MenuVersionBranchTarget[];
  /** Set when this version was itself created by a rollback. */
  reverts: MenuVersionRevertRef | null;
  createdBy: MenuGovernancePerson;
  approvedBy: MenuGovernancePerson | null;
  publishedBy: MenuGovernancePerson | null;
  _count: { rules: number };
}

export interface MenuRuleTranslationRow {
  id: string;
  ruleId: string;
  locale: string;
  name: string;
  description: string | null;
}

/** One `(branchId, itemId)` row a version carries. */
export interface BranchMenuRuleRow {
  id: string;
  versionId: string;
  branchId: string;
  itemId: string;
  carried: boolean;
  /** A decimal STRING, or null — inherits `CatalogItem.basePrice`. Never a number. */
  price: string | null;
  /** Empty means every channel. */
  channels: MenuChannel[];
  sortOrder: number | null;
  effectiveFrom: string;
  createdAt: string;
  updatedAt: string;
  branch: { id: string; name: string };
  item: { id: string; name: string; basePrice: string };
  translations: MenuRuleTranslationRow[];
}

/** `GET /menu-governance/versions/:id` — the row plus every rule it carries. */
export interface MenuVersionDetail extends MenuVersionRow {
  rules: BranchMenuRuleRow[];
}

/** `{ items, total, page, limit, pages }` — see the file-level note above. */
export interface MenuVersionList {
  items: MenuVersionRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface MenuVersionsQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: MenuVersionStatus;
  branchId?: string;
}

// --------------------------------------------------------------- preview

/** A row can be several of these at once. */
export type MenuChangeKind =
  | 'ADDED'
  | 'WITHDRAWN'
  | 'REPRICED'
  | 'CHANNELS_CHANGED'
  | 'REORDERED'
  | 'UNCHANGED';

/** One side of the before/after comparison, already `formatMoney`-formatted. */
export interface MenuPreviewSide {
  carried: boolean;
  /** The branch override, or null when it inherits the catalogue price. */
  price: string | null;
  /** What is actually charged — the override, or the catalogue price. */
  effectivePrice: string;
  channels: MenuChannel[];
  sortOrder: number | null;
}

export type MenuPreviewWarningCode =
  | 'MISSING_TRANSLATION'
  | 'LARGE_PRICE_MOVE'
  | 'WITHDRAWN_WHILE_LISTED'
  | 'PRICED_TO_ZERO';

export interface MenuPreviewWarning {
  code: MenuPreviewWarningCode;
  /**
   * The server's own English sentence. NEVER RENDERED — `publish-preview.ts`
   * builds this with no i18n on the backend at all, so putting it on screen
   * would show an Arabic reader an English sentence. `warningSentence()` in
   * `lib/preview.ts` rebuilds the same sentence from `code` plus the change's
   * own `itemName` / `branchName` / `pricePercent`, which the payload already
   * carries as separate fields — see that file for why this is always possible
   * for all four codes without parsing this string.
   */
  message: string;
}

export interface MenuPreviewChange {
  branchId: string;
  branchName: string;
  itemId: string;
  itemName: string;
  /** ISO 4217 code, e.g. `"SAR"`. */
  currency: string;
  kinds: MenuChangeKind[];
  before: MenuPreviewSide;
  after: MenuPreviewSide;
  /** Signed, at the branch's scale. Null when nothing is charged on one side. */
  priceDelta: string | null;
  /** Signed, two places, e.g. `"-40.00"`. Null when the old price was zero, or unchanged. */
  pricePercent: string | null;
  warnings: MenuPreviewWarning[];
}

export interface MenuPreviewSummary {
  branches: number;
  items: number;
  added: number;
  withdrawn: number;
  repriced: number;
  channelsChanged: number;
  reordered: number;
  unchanged: number;
  warnings: number;
  largestPriceMovePercent: string | null;
}

/** `GET /menu-governance/versions/:id/preview`. */
export interface MenuVersionPreview {
  summary: MenuPreviewSummary;
  changes: MenuPreviewChange[];
}

// --------------------------------------------------------- branch assortment

export interface BranchAssortmentItem {
  itemId: string;
  name: string;
  carried: boolean;
  /** The branch override, decimal string, or null when it inherits the catalogue price. */
  price: string | null;
  catalogPrice: string | null;
  channels: MenuChannel[];
  sortOrder: number | null;
  /** Which version this row's rule came from — the answer to "who changed this". */
  versionNo: number;
  effectiveFrom: string;
}

/** `GET /menu-governance/branches/:branchId/assortment`. */
export interface BranchAssortmentResponse {
  branchId: string;
  asOf: string;
  channel: MenuChannel | null;
  items: BranchAssortmentItem[];
}

export interface BranchAssortmentQuery {
  channel?: MenuChannel;
  /** ISO. Omit for now — pass a past or future moment to answer "what were we charging then". */
  asOf?: string;
}

// ------------------------------------------------------------------- endpoints

export const menuGovernanceApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /menu-governance/versions` — `menu_governance.view`. */
    menuVersions: build.query<MenuVersionList, MenuVersionsQuery | void>({
      query: (params) => ({ url: MENU_VERSIONS, params: params ?? undefined }),
      transformResponse: (response: unknown): MenuVersionList => {
        const p = response as Partial<MenuVersionList> | null;
        return {
          items: Array.isArray(p?.items) ? (p!.items as MenuVersionRow[]) : [],
          total: typeof p?.total === 'number' ? p.total : 0,
          page: typeof p?.page === 'number' ? p.page : 1,
          limit: typeof p?.limit === 'number' ? p.limit : 25,
          pages: typeof p?.pages === 'number' ? p.pages : 1,
        };
      },
      providesTags: (result) => [
        { type: 'MenuVersion' as const, id: 'LIST' },
        ...(result?.items ?? []).map((v) => ({ type: 'MenuVersion' as const, id: v.id })),
      ],
    }),

    /** `GET /menu-governance/versions/:id` — `menu_governance.view`. */
    menuVersion: build.query<MenuVersionDetail | null, string>({
      query: (id) => ({ url: menuVersionPath(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as MenuVersionDetail) : null,
      providesTags: (_result, _error, id) => [{ type: 'MenuVersion' as const, id }],
    }),

    /**
     * `GET /menu-governance/versions/:id/preview` — `menu_governance.view`, not
     * `.publish`: seeing the diff is what lets a branch manager who cannot sign
     * it object before it lands.
     */
    menuVersionPreview: build.query<MenuVersionPreview | null, string>({
      query: (id) => ({ url: menuVersionPreviewPath(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as MenuVersionPreview) : null,
      providesTags: (_result, _error, id) => [{ type: 'MenuVersion' as const, id }],
    }),

    /** `GET /menu-governance/branches/:branchId/assortment` — `menu_governance.view`. */
    branchAssortment: build.query<
      BranchAssortmentResponse | null,
      { branchId: string } & BranchAssortmentQuery
    >({
      query: ({ branchId, ...params }) => ({
        url: branchAssortmentPath(branchId),
        params,
      }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as BranchAssortmentResponse) : null,
      providesTags: (_result, _error, { branchId }) => [
        { type: 'MenuVersion' as const, id: `assortment:${branchId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMenuVersionsQuery,
  useMenuVersionQuery,
  useMenuVersionPreviewQuery,
  useBranchAssortmentQuery,
} = menuGovernanceApi;

/**
 * One version changed — renamed, its rules edited, or its lifecycle moved.
 * Invalidates the list (status, branch and rule counts all live there) and
 * the version's own cache entry.
 */
export function invalidateMenuVersion(id?: string) {
  return menuGovernanceApi.util.invalidateTags([
    { type: 'MenuVersion' as const, id: 'LIST' },
    ...(id ? [{ type: 'MenuVersion' as const, id }] : []),
  ]);
}

/**
 * A publish or a rollback landed, which can change what ANY branch it targeted
 * now carries. Call alongside {@link invalidateMenuVersion} after `publish`,
 * `cancel` and `rollback` — the three lifecycle steps that can move what a
 * branch's own history screen reads.
 */
export function invalidateBranchAssortment(branchIds: string[]) {
  return menuGovernanceApi.util.invalidateTags(
    branchIds.map((branchId) => ({ type: 'MenuVersion' as const, id: `assortment:${branchId}` })),
  );
}
